// WHITE-LABEL: NATIVE UI BEHAVIORS
// Small DOM adapters retained while older business components move to Ui*
// components. This module has no dependency on a presentation framework.
const stores = {
  autocomplete: new WeakMap(), datepicker: new WeakMap(), dropdown: new WeakMap(),
  formSelect: new WeakMap(), modal: new WeakMap(), sidenav: new WeakMap(), tooltip: new WeakMap(),
};

const elementsOf = (value) => {
  if (!value) return [];
  if (value instanceof Element) return [value];
  return Array.from(value).filter((item) => item instanceof Element);
};

const initialize = (value, store, create) => {
  const elements = elementsOf(value);
  const instances = elements.map((element) => {
    store.get(element)?.destroy?.();
    const instance = create(element);
    store.set(element, instance);
    return instance;
  });
  return value instanceof Element ? instances[0] : instances;
};

const noopControl = (store) => ({
  init(value) {
    return initialize(value, store, (element) => ({ el: element, destroy() { store.delete(element); } }));
  },
  getInstance(element) { return store.get(element) || null; },
});

const FormSelect = noopControl(stores.formSelect);

const Tooltip = {
  init(value) {
    return initialize(value, stores.tooltip, (element) => {
      const label = element.dataset.tooltip;
      if (label && !element.getAttribute("title")) element.setAttribute("title", label);
      return { el: element, destroy() { stores.tooltip.delete(element); } };
    });
  },
  getInstance(element) { return stores.tooltip.get(element) || null; },
};

const targetFor = (trigger) => {
  const id = trigger?.dataset?.target || trigger?.getAttribute("href")?.replace(/^#/, "");
  return id ? document.getElementById(id) : null;
};

const Dropdown = {
  init(value) {
    return initialize(value, stores.dropdown, (trigger) => {
      const menu = targetFor(trigger);
      const close = () => { menu?.classList.remove("is-open"); trigger.setAttribute("aria-expanded", "false"); };
      const toggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const opening = !menu?.classList.contains("is-open");
        document.querySelectorAll(".dropdown-content.is-open").forEach((item) => item.classList.remove("is-open"));
        menu?.classList.toggle("is-open", opening);
        trigger.setAttribute("aria-expanded", String(opening));
      };
      const outside = (event) => { if (!trigger.contains(event.target) && !menu?.contains(event.target)) close(); };
      const keyboard = (event) => {
        if (event.key === "Escape") {
          close();
          trigger.focus();
        }
        if (["Enter", " ", "ArrowDown"].includes(event.key) && !menu?.classList.contains("is-open")) {
          event.preventDefault();
          toggle(event);
          menu?.querySelector("a, button, [tabindex]:not([tabindex='-1'])")?.focus();
        }
      };
      trigger.setAttribute("aria-haspopup", "menu");
      trigger.setAttribute("aria-expanded", "false");
      trigger.addEventListener("click", toggle);
      trigger.addEventListener("keydown", keyboard);
      menu?.addEventListener("keydown", keyboard);
      document.addEventListener("click", outside);
      return { el: trigger, dropdownEl: menu, close, open: () => menu?.classList.add("is-open"), destroy() { trigger.removeEventListener("click", toggle); trigger.removeEventListener("keydown", keyboard); menu?.removeEventListener("keydown", keyboard); document.removeEventListener("click", outside); stores.dropdown.delete(trigger); } };
    });
  },
  getInstance(element) { return stores.dropdown.get(element) || null; },
};

const Modal = {
  init(value) {
    return initialize(value, stores.modal, (element) => {
      let previousFocus = null;
      const focusable = () => [...element.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")];
      const open = () => {
        previousFocus = document.activeElement;
        element.classList.add("is-open");
        element.setAttribute("aria-hidden", "false");
        document.body.classList.add("has-native-modal");
        window.requestAnimationFrame(() => (focusable()[0] || element).focus());
      };
      const close = () => {
        element.classList.remove("is-open");
        element.setAttribute("aria-hidden", "true");
        document.body.classList.remove("has-native-modal");
        previousFocus?.focus?.();
      };
      const triggerHandler = (event) => { event.preventDefault(); open(); };
      const closeHandler = (event) => { if (event.target.closest(".modal-close")) close(); };
      const keyboard = (event) => {
        if (!element.classList.contains("is-open")) return;
        if (event.key === "Escape") {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== "Tab") return;
        const controls = focusable();
        if (!controls.length) {
          event.preventDefault();
          element.focus();
          return;
        }
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      };
      const triggers = [...document.querySelectorAll(".modal-trigger")].filter((trigger) => targetFor(trigger) === element);
      triggers.forEach((trigger) => trigger.addEventListener("click", triggerHandler));
      element.addEventListener("click", closeHandler);
      element.addEventListener("keydown", keyboard);
      element.setAttribute("role", "dialog");
      element.setAttribute("aria-modal", "true");
      if (!element.hasAttribute("tabindex")) element.setAttribute("tabindex", "-1");
      element.setAttribute("aria-hidden", element.classList.contains("is-open") ? "false" : "true");
      return { el: element, open, close, destroy() { triggers.forEach((trigger) => trigger.removeEventListener("click", triggerHandler)); element.removeEventListener("click", closeHandler); element.removeEventListener("keydown", keyboard); stores.modal.delete(element); } };
    });
  },
  getInstance(element) { return stores.modal.get(element) || null; },
};

const isoDate = (value) => value instanceof Date && !Number.isNaN(value.valueOf())
  ? value.toISOString().slice(0, 10) : null;

const Datepicker = {
  init(value, options = {}) {
    return initialize(value, stores.datepicker, (element) => {
      element.type = "date";
      const min = isoDate(options.minDate);
      const max = isoDate(options.maxDate);
      if (min) element.min = min;
      if (max) element.max = max;
      const initial = isoDate(options.defaultDate);
      if (initial && options.setDefaultDate && !element.value) element.value = initial;
      const select = () => { if (element.value && options.onSelect) options.onSelect(new Date(`${element.value}T00:00:00`)); };
      element.addEventListener("change", select);
      return { el: element, destroy() { element.removeEventListener("change", select); stores.datepicker.delete(element); } };
    });
  },
  getInstance(element) { return stores.datepicker.get(element) || null; },
};

const Autocomplete = {
  init(value, options = {}) {
    return initialize(value, stores.autocomplete, (element) => {
      let data = options.data || {};
      let activeIndex = -1;
      const container = document.createElement("ul");
      container.className = "autocomplete-content dropdown-content native-autocomplete";
      element.insertAdjacentElement("afterend", container);

      const close = () => { activeIndex = -1; container.classList.remove("is-open"); };
      const position = () => {
        if (container.parentNode !== document.body) return;
        const bounds = element.getBoundingClientRect();
        container.style.position = "fixed";
        container.style.left = `${bounds.left}px`;
        container.style.top = `${bounds.bottom + 4}px`;
        container.style.width = `${bounds.width}px`;
      };
      const choose = (label) => {
        element.value = label;
        element.dispatchEvent(new Event("input", { bubbles: true }));
        options.onAutocomplete?.(label);
        close();
      };
      const render = () => {
        const query = element.value.toLowerCase();
        const minimum = options.minLength ?? 1;
        const matches = Object.keys(data).filter((label) => query.length >= minimum && label.toLowerCase().includes(query)).slice(0, 50);
        container.replaceChildren(...matches.map((label) => {
          const item = document.createElement("li");
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = label;
          button.addEventListener("click", () => choose(label));
          item.appendChild(button);
          return item;
        }));
        activeIndex = -1;
        position();
        container.classList.toggle("is-open", matches.length > 0);
      };
      const keyboard = (event) => {
        const buttons = [...container.querySelectorAll("button")];
        if (event.key === "Escape") { close(); return; }
        if (!buttons.length || !["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;
        event.preventDefault();
        if (event.key === "Enter" && activeIndex >= 0) { buttons[activeIndex].click(); return; }
        activeIndex = event.key === "ArrowDown"
          ? (activeIndex + 1) % buttons.length
          : (activeIndex <= 0 ? buttons.length - 1 : activeIndex - 1);
        buttons.forEach((button, index) => button.setAttribute("aria-selected", String(index === activeIndex)));
        buttons[activeIndex].scrollIntoView({ block: "nearest" });
      };
      const outside = (event) => { if (event.target !== element && !container.contains(event.target)) close(); };
      element.addEventListener("input", render);
      element.addEventListener("focus", render);
      element.addEventListener("keydown", keyboard);
      document.addEventListener("click", outside);
      window.addEventListener("resize", position);
      window.addEventListener("scroll", position, true);

      const dropdown = {
        el: element,
        dropdownEl: container,
        _getDropdownPosition: () => ({ height: container.scrollHeight, verticalAlignment: "top", y: 0 }),
        destroy: close,
      };
      return {
        container, dropdown, el: element,
        close, open: render, updateData(nextData) { data = nextData || {}; render(); },
        destroy() { element.removeEventListener("input", render); element.removeEventListener("focus", render); element.removeEventListener("keydown", keyboard); document.removeEventListener("click", outside); window.removeEventListener("resize", position); window.removeEventListener("scroll", position, true); container.remove(); stores.autocomplete.delete(element); },
      };
    });
  },
  getInstance(element) { return stores.autocomplete.get(element) || null; },
};

const Sidenav = {
  init(value) {
    return initialize(value, stores.sidenav, (element) => {
      const id = element.id;
      const triggers = [...document.querySelectorAll(".sidenav-trigger")].filter((trigger) => trigger.dataset.target === id);
      const open = () => element.classList.add("is-open");
      const close = () => element.classList.remove("is-open");
      const toggle = (event) => { event.preventDefault(); element.classList.toggle("is-open"); };
      const keyboard = (event) => { if (event.key === "Escape") close(); };
      triggers.forEach((trigger) => trigger.addEventListener("click", toggle));
      document.addEventListener("keydown", keyboard);
      return { el: element, open, close, destroy() { triggers.forEach((trigger) => trigger.removeEventListener("click", toggle)); document.removeEventListener("keydown", keyboard); stores.sidenav.delete(element); } };
    });
  },
  getInstance(element) { return stores.sidenav.get(element) || null; },
};

const toast = ({ html = "", classes = "" } = {}) => {
  let region = document.getElementById("native-ui-toasts");
  if (!region) {
    region = document.createElement("div");
    region.id = "native-ui-toasts";
    region.className = "native-ui-toasts";
    region.setAttribute("aria-live", "polite");
    document.body.appendChild(region);
  }
  const item = document.createElement("div");
  item.className = `native-ui-toast ${classes}`.trim();
  item.textContent = String(html).replace(/<[^>]*>/g, "");
  region.appendChild(item);
  window.setTimeout(() => item.remove(), 4000);
  return { dismiss: () => item.remove() };
};

const nativeUi = {
  Autocomplete, Datepicker, Dropdown, FormSelect, Modal, Sidenav, Tooltip, toast,
  textareaAutoResize(element) {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  },
  updateTextFields() {},
};

export default nativeUi;
